import React, { useState, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
    DeletePhotoService,
    StartMobileUploadSessionService,
    SubmitPhotoService,
    UploadPhotoService,
} from '../../../services/BiostarEnrollmentService';
import { closeComponentModal } from '../../../utils/showUploadPhotoModal';
import SocketConnection from '../../../utils/SocketConnection';
import PhotoCapturePreview from './PhotoCapturePreview';
import PhotoUploadOptions from './PhotoUploadOptions';
import PhotoUploadSuccess from './PhotoUploadSuccess';
import { Banner } from '../../library/Banner';
import { Container } from '../../library/Container';
import { Description, Title } from '../../library/text';
import {
    buildLocalMobileUploadUrl,
    buildMobileUploadUrl,
    buildTempPhotoUrl,
    captureFrameFromVideo,
    CAPTURE_STAGES,
    isDebugOrigin,
    MAX_UPLOAD_SIZE_BYTES,
    PHOTO_RESULT_CODES,
    PHOTO_STAGES,
    runLocalChecks,
    stopMediaStream,
    useBiostarAccount,
} from './utils';

/**
 * The "Upload Photo" flow: capture or upload a headshot, then submit it to Biostar
 * for validation and face enrollment. Render it via showPhotoEnrollmentDialog().
 *
 * A state machine over `stage` (see PHOTO_STAGES), shown as three screens:
 * PhotoUploadOptions -> PhotoCapturePreview -> PhotoUploadSuccess. This component
 * owns only what spans those screens - the webcam stream and the mobile-relay
 * subscription both have to be torn down whichever screen is showing.
 */
function PhotoEnrollmentDialog({ onPhotoUpdated, hasPhoto }) {
    const isMobile = useIsMobile();
    const [stage, setStage] = useState(PHOTO_STAGES.OPTIONS);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [localChecks, setLocalChecks] = useState({ format: false, size: false });
    const [errorMessage, setErrorMessage] = useState('');
    const [biostarError, setBiostarError] = useState('');
    const [mobileUploadUrl, setMobileUploadUrl] = useState(null);
    const [localDebugUrl, setLocalDebugUrl] = useState(null);
    const [isPhotoIncoming, setIsPhotoIncoming] = useState(false);
    const [isRefreshingQr, setIsRefreshingQr] = useState(false);
    const { isAccountReady, isLoading, errorMessage: accountError, retry: initBiostarAccount } = useBiostarAccount();

    const hasPhotoError = !localChecks.format || !localChecks.size || !!biostarError;
    const canSubmit = stage === PHOTO_STAGES.PREVIEW && isAccountReady && !hasPhotoError;

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const subscriptionRef = useRef(null);
    const isMountedRef = useRef(true);

    /** Only applicable if on desktop, subscribe to websocket to detect when uploading is done: */
    useEffect(() => {
        isMountedRef.current = true;

        if (!isMobile) {
            startMobileUploadSession();
        }
        return () => {
            // Set before the teardown below, so anything still awaiting inside
            // startMobileUploadSession/handleTakePhoto releases what it acquired
            // instead of storing it in a ref nothing will clean up again.
            isMountedRef.current = false;
            stopMediaStream(streamRef.current);
            streamRef.current = null;
            closeMobileSubscription();
        };
    }, [isMobile]);

    /**
     * Clears all local state back to the initial "options" screen.
     */
    const resetState = () => {
        setStage(PHOTO_STAGES.OPTIONS);
        setPhotoUrl(null);
        setLocalChecks({ format: false, size: false });
        setErrorMessage('');
        setBiostarError('');
    };

    /**
     * Opens a websocket subscription and renders a QR code so the student can
     * continue the photo capture on their mobile device.
     */
    const startMobileUploadSession = async () => {
        try {
            if (!SocketConnection.ws) {
                await SocketConnection.connect();
            }

            const { data } = await StartMobileUploadSessionService();
            const { token, securityToken } = data;

            if (!isMountedRef.current) {
                return;
            }

            setMobileUploadUrl(buildMobileUploadUrl(securityToken));

            if (isDebugOrigin()) {
                // Only set this for debugging locally/staging2 to get the image faster.
                setLocalDebugUrl(buildLocalMobileUploadUrl(securityToken));
            }

            const topic = await SocketConnection.subscribe(`photoRelay:${token}`);

            // The dialog can close while that subscribe is in flight, after the unmount
            // cleanup has already run - close this topic rather than leaving it open with
            // handlers that would setState on an unmounted component.
            if (!isMountedRef.current) {
                if (topic?.close) {
                    topic.close();
                }
                return;
            }

            subscriptionRef.current = topic;

            topic.on('photo-received', () => {
                setIsPhotoIncoming(true);
            });

            topic.on('photo-relayed', (payload) => {
                applyUploadResult(payload);
                closeMobileSubscription();
            });

            topic.on('error', () => {
                setErrorMessage('The mobile upload connection dropped. Please close this dialog and try again.');
                setIsPhotoIncoming(false);
                setMobileUploadUrl(null);
                closeMobileSubscription();
            });
        } catch (err) {
            console.error('PhotoEnrollmentDialog->startMobileUploadSession->Error', err);

            if (!isMountedRef.current) {
                return;
            }

            /**
             * Without a message the QR column just renders empty, which reads as a glitch
             * rather than a failure the student can do something about.
             */
            setErrorMessage("Couldn't set up the mobile upload. Use Upload Photo or Take Photo instead.");
            setMobileUploadUrl(null);
        }
    };

    /**
     * Tears down the mobile upload websocket subscription, if one is open. Closing an
     * already-dead socket can throw, and this runs from the unmount cleanup where nothing
     * would catch it, so the ref is always cleared regardless.
     */
    const closeMobileSubscription = () => {
        try {
            if (subscriptionRef.current?.close) {
                subscriptionRef.current.close();
            }
        } catch (err) {
            console.error('PhotoEnrollmentDialog->closeMobileSubscription->Error', err);
        }

        subscriptionRef.current = null;
    };

    /**
     * Tears down the current mobile upload session, if any, and mints a fresh one.
     */
    const restartMobileUploadSession = async () => {
        closeMobileSubscription();
        setMobileUploadUrl(null);
        setIsPhotoIncoming(false);
        await startMobileUploadSession();
    };

    /**
     * Manual "Refresh QR code" action for when the one on screen has gone stale.
     */
    const handleRefreshQr = async () => {
        setIsRefreshingQr(true);
        try {
            await restartMobileUploadSession();
        } finally {
            if (isMountedRef.current) {
                setIsRefreshingQr(false);
            }
        }
    };

    /**
     * Validates a File picked on desktop/mobile and, if it passes, uploads it
     * for server-side validation.
     */
    const handleFileSelect = async (file) => {
        closeMobileSubscription();

        // Only a file the API would refuse anyway is stopped here - anything smaller is sent
        // so the checklist can flag its format/size against the photo itself.
        if (file.size > MAX_UPLOAD_SIZE_BYTES) {
            setErrorMessage('That photo is too large to upload. Please choose one under 25MB.');
            return;
        }

        await uploadPhoto(file);
    };

    /**
     * Uploads a photo file/blob to the server, which saves it to a temp slot and
     * validates it against Biostar right away.
     */
    const uploadPhoto = async (fileOrBlob) => {
        setErrorMessage('');
        setStage(PHOTO_STAGES.UPLOADING);

        try {
            const formData = new FormData();
            formData.append('image', fileOrBlob, 'photo.jpg');
            const response = await UploadPhotoService(formData);

            if (!isMountedRef.current) {
                return;
            }

            applyUploadResult(response?.data);
        } catch (err) {
            console.error('PhotoEnrollmentDialog->uploadPhoto->Error', err);

            if (!isMountedRef.current) {
                return;
            }

            setErrorMessage('Something went wrong uploading your photo. Please try again.');
            setStage(PHOTO_STAGES.OPTIONS);
        }
    };

    /**
     * Shared entry point for every upload result - desktop file pick, webcam capture
     * and mobile relay all land here, so the preview and checklist can't diverge.
     *
     * LOCAL_INVALID_FORMAT/LOCAL_FILE_TOO_LARGE are the server's own format/size checks,
     * which neither surface pre-screens, so they set the matching checklist row and the
     * top banner. Any other failing code is Biostar's verdict on the photo, which goes
     * to biostarError so it renders inside the checklist instead.
     */
    const applyUploadResult = ({ photoPath, code, message, success } = {}) => {
        setPhotoUrl(buildTempPhotoUrl(photoPath));

        if (code === PHOTO_RESULT_CODES.LOCAL_INVALID_FORMAT || code === PHOTO_RESULT_CODES.LOCAL_FILE_TOO_LARGE) {
            setLocalChecks({
                format: code !== PHOTO_RESULT_CODES.LOCAL_INVALID_FORMAT,
                size: code !== PHOTO_RESULT_CODES.LOCAL_FILE_TOO_LARGE,
            });
            setErrorMessage(message || 'That photo doesn’t meet the photo requirements.');
            setBiostarError('');
        } else {
            setLocalChecks({ format: true, size: true });
            setErrorMessage('');
            setBiostarError(success === false ? message || 'Biostar rejected this photo.' : '');
        }

        setStage(PHOTO_STAGES.PREVIEW);
    };

    /**
     * Starts the device webcam and switches the dialog into live-capture mode.
     */
    const handleTakePhoto = async () => {
        // Same as the file-pick path: a relay landing mid-capture would swap the stage
        // out from under the live <video> and strand its stream.
        closeMobileSubscription();

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });

            // The permission prompt can outlive the dialog, and nothing would stop a
            // stream handed over after the unmount cleanup already ran.
            if (!isMountedRef.current) {
                stopMediaStream(stream);
                return;
            }

            streamRef.current = stream;
            setStage(PHOTO_STAGES.WEBCAM);

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error('PhotoEnrollmentDialog->handleTakePhoto->Error', err);
            setErrorMessage("Couldn't access your webcam. Please check your browser permissions.");
        }
    };

    /**
     * Grabs the current webcam frame onto a canvas, converts it to a JPEG blob,
     * stops the camera stream, and uploads it for validation.
     */
    const handleCapture = async () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;

        if (!video || !canvas || stage !== PHOTO_STAGES.WEBCAM) {
            return;
        }

        /**
         * Leaves the webcam stage immediately, so a second click can't start a second
         * capture and upload while this one is still encoding.
         */
        setStage(PHOTO_STAGES.UPLOADING);

        let blob;
        try {
            blob = await captureFrameFromVideo(video, canvas);
        } catch (err) {
            console.error('PhotoEnrollmentDialog->handleCapture->Error', err);
        }

        stopMediaStream(streamRef.current);
        streamRef.current = null;

        if (!blob) {
            setErrorMessage("Couldn't capture a photo. Please try again.");
            setStage(PHOTO_STAGES.OPTIONS);
            return;
        }

        setLocalChecks(runLocalChecks('image/jpeg', blob.size));
        await uploadPhoto(blob);
    };

    /**
     * Stops the live webcam stream and returns to the requirements/options screen
     * without a captured photo.
     */
    const handleCancelWebcam = () => {
        stopMediaStream(streamRef.current);
        streamRef.current = null;
        setStage(PHOTO_STAGES.OPTIONS);
    };

    /**
     * Discards the current photo and returns to the requirements/options screen.
     * Also restarts the mobile upload session so a fresh QR/token is shown -
     * the previous one's subscription is already closed (torn down as soon as
     * that photo came in), so re-scanning the stale QR would fail server-side.
     */
    const handleRetake = async () => {
        resetState();

        const pendingCalls = [];

        if (!isMobile) {
            pendingCalls.push(restartMobileUploadSession());
        }

        if (!isAccountReady && !isLoading) {
            pendingCalls.push(initBiostarAccount());
        }

        await Promise.all(pendingCalls);
    };

    /**
     * Enrolls whatever photo was already uploaded and validated - the image
     * itself already lives server-side, so this call doesn't send it again.
     */
    const handleSubmit = async () => {
        if (!canSubmit) {
            return;
        }

        setErrorMessage('');
        setStage(PHOTO_STAGES.SUBMITTING);

        try {
            const response = await SubmitPhotoService();
            const body = response?.data;

            if (!isMountedRef.current) {
                return;
            }

            if (body?.success === false) {
                const step = body?.step ? ` (${body.step})` : '';
                setErrorMessage(`${body.message || 'Biostar rejected this photo.'}${step}`);
                setStage(PHOTO_STAGES.PREVIEW);
                return;
            }

            setStage(PHOTO_STAGES.SUCCESS);

            if (onPhotoUpdated) {
                onPhotoUpdated(body);
            }
        } catch (err) {
            console.error('PhotoEnrollmentDialog->handleSubmit->Error', err);

            if (!isMountedRef.current) {
                return;
            }

            const step = err?.response?.data?.step ? ` (${err.response.data.step})` : '';
            const message = err?.response?.data?.message || 'Something went wrong submitting your photo.';
            setErrorMessage(`${message}${step}`);
            setStage(PHOTO_STAGES.PREVIEW);
        }
    };

    /**
     * Debug-only: unenrols the student's photo. Firing a Swal from inside this dialog
     * destroys it (SweetAlert2 shows one popup at a time), which is fine on the confirm
     * path since a successful delete closes the dialog anyway.
     */
    const handleDeletePhoto = async () => {
        const confirmation = await Swal.fire({
            title: 'Delete your photo?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        });

        if (!confirmation.isConfirmed) {
            return;
        }

        try {
            await DeletePhotoService();

            if (onPhotoUpdated) {
                onPhotoUpdated();
            }
        } catch (error) {
            console.error('PhotoEnrollmentDialog->handleDeletePhoto->Error', error);
            Swal.fire({ icon: 'error', title: 'Could not delete the photo' });
        }
    };

    /**
     * Stops the webcam/mobile subscription and closes the surrounding Swal popover.
     * Only needed for the "Done" button now - dismissal otherwise goes through
     * Swal's own close button/outside-click/escape, which unmounts this component
     * and runs the same cleanup via the effect above.
     */
    const handleClose = () => {
        stopMediaStream(streamRef.current);
        streamRef.current = null;
        closeMobileSubscription();
        closeComponentModal();
    };

    const isCaptureStage = CAPTURE_STAGES.includes(stage);

    return (
        <Container>
            {stage !== PHOTO_STAGES.SUCCESS && (
                <>
                    <Title>Upload Photo</Title>
                    <Description>
                        {isMobile
                            ? 'We require a photo of your face to register into the system to allow it to recognize you and grant entry to campus.'
                            : 'Please upload a recent headshot of yourself to register you in our system for identification and campus access.'}
                    </Description>
                </>
            )}

            {(errorMessage || accountError) && <Banner>{errorMessage || accountError}</Banner>}

            {stage === PHOTO_STAGES.OPTIONS && (
                <PhotoUploadOptions
                    isMobile={isMobile}
                    mobileUploadUrl={mobileUploadUrl}
                    localDebugUrl={localDebugUrl}
                    isPhotoIncoming={isPhotoIncoming}
                    isRefreshingQr={isRefreshingQr}
                    onFileSelect={handleFileSelect}
                    onTakePhoto={handleTakePhoto}
                    onDeletePhoto={isDebugOrigin() && hasPhoto ? handleDeletePhoto : null}
                    onRefreshQr={handleRefreshQr}
                />
            )}

            {isCaptureStage && (
                <PhotoCapturePreview
                    stage={stage}
                    isMobile={isMobile}
                    photoUrl={photoUrl}
                    localChecks={localChecks}
                    biostarError={biostarError}
                    videoRef={videoRef}
                    canvasRef={canvasRef}
                    canSubmit={canSubmit}
                    onCapture={handleCapture}
                    onBack={stage === PHOTO_STAGES.WEBCAM ? handleCancelWebcam : handleRetake}
                    onSubmit={handleSubmit}
                />
            )}

            {stage === PHOTO_STAGES.SUCCESS && <PhotoUploadSuccess onClose={handleClose} />}
        </Container>
    );
}

export default PhotoEnrollmentDialog;
